import * as React from "react";
import "./statusTaskModule.scss";
import {getSentNotification} from "../../apis/messageListApi";
import {
    ArrowRightIcon,
    Button,
    Divider,
    Dropdown,
    Flex,
    Header,
    Loader,
    Menu,
    Pill,
    PillGroup,
    Table,
    Text,
    Tooltip,
} from "@fluentui/react-northstar";
import {getInitAdaptiveCard, updateCard} from "../AdaptiveCard/adaptiveCard";
import {Bar, Chart, Pie} from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {getBlobImage} from "../../apis/blobImageApi";
import {ISentMessage} from "../../Interfaces/Message";
import {languageObject} from "../../translations";
import {IFeedback} from "../../Interfaces/IFeedback";
import {AppContext, IAppContextState} from "../../services/AppContext";
import FeedbackApi from "../../apis/feedbackApi";
import moment from "moment";
import GroupApi from "../../apis/groupApi";
import {brandColors} from "../../const/ThemeCustomizations";
import {ChartData} from "chart.js";
import {SentMessagesDetails} from "./SentMessagesDetails/SentMessagesDetails";
import {FileModule} from "../../modules/File.module";

enum MenuPages {
    Configuration = 0,
    Performance = 1,
    Impact = 2,
    Feedbacks = 3,
    Actions = 4,
}

export interface IStatusState {
    menuPage: MenuPages;
    message: ISentMessage;
    loading: boolean;
    teamId?: string;
    currentPreviewLanguage: number;
    notificationFeedbacks: Array<IFeedback>;
    totalFeedbacksDistinctByUserId: number;
    isExporting: boolean;
    teamsTheme: string;
    pieOptions: PieOptions;
    upnContext: string;
    tenantId: string;
    groupId: string;
    channelId: string;
    messageSentDuration: string;
    messageImage: string;
}

interface StatusTaskModuleProps {
    messageId: string;
}

class StatusTaskModule extends React.PureComponent<
    StatusTaskModuleProps,
    IStatusState
> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private readonly card: any;

    constructor(props: StatusTaskModuleProps) {
        super(props);

        this.card = getInitAdaptiveCard();

        this.state = {
            menuPage: MenuPages.Configuration,
            message: {} as any,
            loading: true,
            teamId: "",
            currentPreviewLanguage: -1,
            notificationFeedbacks: new Array<IFeedback>(),
            totalFeedbacksDistinctByUserId: 0,
            isExporting: false,
            teamsTheme: "",
            upnContext: "",
            tenantId: "",
            groupId: "",
            channelId: "",
            messageSentDuration: "",
            messageImage: "",
            pieOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        onClick: () => {
                        },
                        position: "left",
                        labels: {
                            color: "white",
                            font: {
                                family: "Segoe UI",
                                size: 14,
                            },
                        },
                    },
                },
            },
        };
    }

    public componentDidMount = async () => {
        // Settings chart.js
        Chart.register(ChartDataLabels);
        this.setState({
            teamId: this.context.teams.teamId as string,
            teamsTheme: this.context.teams.themeClass as string,
            upnContext: this.context.teams.userMail as string,
            tenantId: this.context.teams.tenantId as string,
            groupId: this.context.teams.groupId as string,
            channelId: this.context.teams.channelId as string,
            pieOptions: {
                ...this.state.pieOptions,
                plugins: {
                    legend: {
                        ...this.state.pieOptions.plugins.legend,
                        labels: {
                            ...this.state.pieOptions.plugins.legend.labels,
                            color:
                                (this.context.teams.themeClass as string) === "defaultContainer"
                                    ? "black"
                                    : "white",
                        },
                    },
                },
            },
        });

        if (!this.props.messageId) throw new Error("Can't find message id");

        let id = this.props.messageId;
        const message = await this.getItem(id);

        if (message.defaultLanguage.imageLink) {
            await getBlobImage(message.defaultLanguage.imageLink).then((response) => {
                message.defaultLanguage.image = response;
            });
        }
        for await (let m of message.translations) {
            if (m.imageLink) {
                await getBlobImage(m.imageLink).then((response) => {
                    m.image = response;
                });
            }
        }
        if (message.groupId) {
            message.group = await GroupApi.getGroup(message.groupId);
        }
        const totalFeedbacksDistinctByUserId =
            await FeedbackApi.getTotalFeedbackDistinctUserId(id);
        this.setState(
            {
                message,
                totalFeedbacksDistinctByUserId,
                loading: false,
            },
            () => this.updateCard()
        );
    };

    private getItem = async (id: string): Promise<ISentMessage> => {
        const message = await getSentNotification(id);
        const sendStart = moment(message.sendingStart);
        const sendEnd = moment(message.sendingEnd);
        message.sendingStart = sendStart.format("lll");
        message.sendingEnd = sendEnd.format("lll");
        let sendingDuration = "";
        if (!!message.sendingStart && !!message.sendingEnd && sendStart < sendEnd) {
            let millisecondsDiff = sendStart.diff(sendEnd, "ms");
            if (millisecondsDiff < 0) millisecondsDiff *= -1;
            sendingDuration = moment.utc(millisecondsDiff).format("HH:mm:ss");
        }
        this.setState({message: message, messageSentDuration: sendingDuration});
        return message;
    };

    private updateCard = () => {
        if (this.card)
            updateCard(
                this.card,
                this.state.message,
                this.context.topics.list,
                this.context.languages.list,
                this.state.currentPreviewLanguage
            );
    };

    public render() {
        // Wait to load
        if (this.state.loading)
            return <Loader styles={{width: "100%", height: "100%"}}/>;

        // Default language name
        const defaultLanguageName =
            this.context.languages.list.find(
                (l) => l.id === this.state.message.defaultLanguage.id
            )?.languageName ?? "";

        // Additional languages names
        const additionalLanguagesNames = new Array<string>();
        this.state.message.translations.forEach((al) => {
            additionalLanguagesNames.push(
                this.context.languages.list.find((l) => l.id === al.id)?.languageName ??
                ""
            );
        });

        // Dropdown items for preview
        const previewLanguagesItems = [
            this.state.message.defaultLanguage,
            ...this.state.message.translations,
        ].map((tm) => ({
            key: tm.id,
            header: languageObject.translate(
                this.context.languages.list.find((l) => l.id === tm.id)?.languageName ??
                ""
            ),
        }));

        // Current shown page
        let currentMenuPage: JSX.Element = <></>;

        // Check if message is sent
        const isMessageSent = this.state.message.numberOfSuccess > 0;

        // Check if message has failed
        const hasMessageFailed = this.state.message.numberOfFailure > 0;

        switch (this.state.menuPage) {
            case MenuPages.Configuration:
                currentMenuPage = this.renderConfigurationTab(
                    defaultLanguageName,
                    additionalLanguagesNames
                );
                break;
            case MenuPages.Performance:
                currentMenuPage = this.renderStatusTab();
                break;
            case MenuPages.Impact:
                currentMenuPage = isMessageSent ? (
                    this.renderImpactTab()
                ) : (
                    <Flex fill gap={"gap.small"} vAlign={"center"} hAlign={"center"}>
                        <Header
                            as={"h3"}
                            content={languageObject.translate("NoDataAvailable")}
                        />
                    </Flex>
                );
                break;
            case MenuPages.Feedbacks:
                let buttonConfig = [
                    this.state.message.defaultLanguage.askFeedback,
                    ...this.state.message.translations.map((b) => b.askFeedback),
                ];
                currentMenuPage = isMessageSent ? (
                    buttonConfig.some((i) => i) ? (
                        this.renderFeedbacksTab()
                    ) : (
                        <Flex fill gap={"gap.small"} vAlign={"center"} hAlign={"center"}>
                            <Header
                                as={"h3"}
                                content={languageObject.translate("FeedBackNoConfigured")}
                            />
                        </Flex>
                    )
                ) : (
                    <Flex fill gap={"gap.small"} vAlign={"center"} hAlign={"center"}>
                        <Header
                            as={"h3"}
                            content={languageObject.translate("NoDataAvailable")}
                        />
                    </Flex>
                );
                break;
            case MenuPages.Actions:
                currentMenuPage = isMessageSent ? this.renderActionsTab() :
                    <Flex fill gap={"gap.small"} vAlign={"center"} hAlign={"center"}>
                        <Header
                            as={"h3"}
                            content={languageObject.translate("NoDataAvailable")}
                        />
                    </Flex>

                break;
            default:
                break;
        }

        return (
            <Flex fill column vAlign="stretch" gap="gap.small">
                {/* Menu */}
                <Menu
                    className={"status_menu"}
                    items={[
                        languageObject.translate("Configuration"),
                        languageObject.translate("Status"),
                        languageObject.translate("Impact"),
                        languageObject.translate("Feedbacks"),
                        languageObject.translate("Actions"),
                    ]}
                    activeIndex={this.state.menuPage}
                    onActiveIndexChange={(e, data) =>
                        this.setState({menuPage: (data as any).activeIndex})
                    }
                    primary
                />
                <Flex className="scrollableContent" gap="gap.small">
                    {this.state.menuPage === MenuPages.Configuration ? (
                        <Flex.Item size={"size.half"}>
                            <Flex fill column>
                                {currentMenuPage}
                            </Flex>
                        </Flex.Item>
                    ) : (
                        <Flex fill column>
                            {currentMenuPage}
                        </Flex>
                    )}
                    {/* Adaptive card */}
                    <Flex.Item
                        size={"size.half"}
                        styles={{
                            maxWidth: "50%",
                            display:
                                this.state.menuPage === MenuPages.Configuration
                                    ? "block"
                                    : "none",
                        }}
                    >
                        <Flex column>
                            <Dropdown
                                fluid
                                items={previewLanguagesItems}
                                value={
                                    previewLanguagesItems[this.state.currentPreviewLanguage + 1]
                                }
                                onChange={(e, data) => {
                                    this.setState(
                                        {
                                            currentPreviewLanguage:
                                                (data as any).highlightedIndex - 1,
                                        },
                                        () => this.updateCard()
                                    );
                                }}
                                styles={{marginTop: "15px", marginBottom: "10px"}}
                            />
                            <div
                                className="adaptiveCardContainer"
                                style={{paddingBottom: "20px"}}
                            />
                        </Flex>
                    </Flex.Item>
                </Flex>
            </Flex>
        );
    }

    renderConfigurationTab = (
        defaultLanguageName: string,
        additionalLanguagesNames: Array<string>
    ) => {
        return (
            <>
                <Header
                    className="status_title"
                    styles={{marginTop: "10px !important"}}
                    as={"h3"}
                    content={languageObject.translate("DefaultLanguage")}
                />
                <Pill className={"pill"} rectangular>
                    {languageObject.translate(defaultLanguageName)}
                </Pill>
                <Header
                    className="status_title"
                    as={"h3"}
                    content={languageObject.translate("AdditionalLanguages")}
                />
                {additionalLanguagesNames.length > 0 ? (
                    <PillGroup>
                        {additionalLanguagesNames.map((m) => (
                            <Pill
                                key={"message_language_" + m}
                                rectangular
                                className={"pill"}
                            >
                                {languageObject.translate(m)}
                            </Pill>
                        ))}
                    </PillGroup>
                ) : (
                    <Text
                        style={{marginLeft: "5px"}}
                        content={languageObject.translate("NoAdditionalLanguage")}
                    />
                )}
                <Header
                    className="status_title"
                    as={"h3"}
                    content={languageObject.translate("Audience")}
                />
                <PillGroup>
                    <Pill
                        key={"message-group" + this.state.message.groupId}
                        rectangular
                        className={"pill"}
                    >
                        {!this.state.message.groupId
                            ? languageObject.translate("AllUsersLabel")
                            : this.state.message.group?.displayName}
                    </Pill>
                </PillGroup>
                <Header
                    className="status_title"
                    as={"h3"}
                    content={languageObject.translate("Topics")}
                />
                <PillGroup>
                    {this.state.message.topicsId.map((tid) => (
                        <Pill key={tid} rectangular className={"pill"}>
                            {this.context.topics.list.find((t) => t.id === tid)?.title}
                        </Pill>
                    ))}
                </PillGroup>
            </>
        );
    };

    renderStatusTab = () => {
        return (
            <>
                <Flex vAlign={"center"} gap={"gap.large"} styles={{marginTop: "-10px"}}>
                    <Flex vAlign={"center"} gap={"gap.small"}>
                        <Flex column>
                            <Header
                                className="status_title"
                                as={"h3"}
                                content={languageObject.translate("SendingStarted")}
                            />
                            <Pill
                                className={"pill"}
                                rectangular
                                content={this.state.message.sendingStart}
                            />
                        </Flex>
                        <ArrowRightIcon styles={{marginTop: "50px"}}/>
                        <Flex column>
                            <Header
                                className="status_title"
                                as={"h3"}
                                content={languageObject.translate("SendingCompleted")}
                            />
                            {this.state.message.sendingEnd ? (
                                <Pill
                                    className={"pill"}
                                    rectangular
                                    content={this.state.message.sendingEnd}
                                />
                            ) : (
                                <Text
                                    style={{marginLeft: "5px"}}
                                    content={languageObject.translate("MessageInProgress")}
                                />
                            )}
                        </Flex>
                    </Flex>
                    <Flex column>
                        <Header
                            className="status_title"
                            as={"h3"}
                            content={languageObject.translate("Duration")}
                        />
                        {this.state.messageSentDuration !== "" ? (
                            <Pill
                                className={"pill"}
                                rectangular
                                content={this.state.messageSentDuration}
                            />
                        ) : (
                            <Text
                                style={{marginLeft: "5px"}}
                                content={languageObject.translate("Unavailable")}
                            />
                        )}
                    </Flex>
                </Flex>
                <Divider styles={{marginTop: "10px"}}/>
                <Header
                    as={"h3"}
                    styles={{marginTop: "10px", marginBottom: "5px"}}
                    content={languageObject.translate("SentMessages")}
                />
                <SentMessagesDetails
                    message={this.state.message}
                    numberOfSuccess={this.state.message.numberOfSuccess}
                    numberOfFailure={this.state.message.numberOfFailure}
                    onResendMessage={this.getItem}
                />
            </>
        );
    };

    renderImpactTab = () => {
        const totalMessages = this.state.message.numberOfSuccess;
        const readMessages = this.state.message.numberToRead;
        let unreadMessages = this.state.message.numberOfSuccess - this.state.message.numberToRead;
        if (unreadMessages < 0) unreadMessages = 0;
        const data: ChartData<"pie"> = {
            labels: [
                languageObject.translate("ReadMessages") + " (" + readMessages + ")",
                languageObject.translate("NotReadMessages") + " (" + unreadMessages + ")",
            ],
            datasets: [
                {
                    data: [readMessages, unreadMessages],
                    backgroundColor: [
                        brandColors[300],
                        brandColors[500],
                    ],
                    hoverBackgroundColor: [
                        brandColors[300],
                        brandColors[500],
                    ],
                    borderWidth: 0,
                    datalabels: {
                        color: 'white',
                        formatter: function (value: number) {
                            return ((value / totalMessages) * 100).toFixed(0) + '%';
                        }
                    }
                },
            ]
        };

        return (
            <Flex column fill vAlign={"center"} styles={{overflowX: "hidden"}}>
                <Pie datatype={"pie"} data={data} options={this.state.pieOptions as any}
                     plugins={[ChartDataLabels] as any}
                     style={{maxHeight: "70%"}}/>
            </Flex>
        );
    }

    renderFeedbacksTab = () => {
        const total = this.state.message.numberOfSuccess;
        const totalFeedback = this.state.totalFeedbacksDistinctByUserId;
        let totalNotReceived = total - totalFeedback;
        if (totalNotReceived < 0) totalNotReceived = 0;
        // Pie data
        const data = {
            labels: [
                languageObject.translate("ReceivedFeedbacks") +
                " (" +
                totalFeedback +
                ")",
                languageObject.translate("NotReceivedFeedbacks") +
                " (" +
                totalNotReceived +
                ")",
            ],
            datasets: [
                {
                    data: [totalFeedback, totalNotReceived],
                    backgroundColor: [brandColors[300], brandColors[500]],
                    hoverBackgroundColor: [brandColors[300], brandColors[500]],
                    borderWidth: 0,
                    datalabels: {
                        color: "white",
                        formatter: function (value: number) {
                            return ((value / total) * 100).toFixed(2) + "%";
                        },
                    },
                },
            ],
        };

        // Export button
        const exportButton = (
            <Button
                fluid
                disabled={!this.state.totalFeedbacksDistinctByUserId || this.state.isExporting}
                primary
                loading={this.state.isExporting}
                onClick={async () => {
                    this.setState({isExporting: true});
                    const blob = await FeedbackApi.exportFeedbacks(this.state.message.id!);
                    FileModule.downloadFile(blob, "export_feedback.xlsx");
                    this.setState({isExporting: false});
                }}
                content={languageObject.translate("ExportFeedbacks")}
            />
        )
        return (
            <Flex fill column gap={"gap.medium"} styles={{marginBottom: "30px"}}>
                <Flex fill gap={"gap.small"} vAlign={"center"} styles={{overflowX: "hidden"}}>
                    <Pie
                        data={data}
                        options={this.state.pieOptions as any}
                        plugins={[ChartDataLabels] as any}
                        style={{maxHeight: "80%"}}
                    />
                </Flex>
                <Flex>
                    {
                        !this.state.totalFeedbacksDistinctByUserId ?
                            <Tooltip align={"center"}
                                     content={languageObject.translate("NoAvailableFeedback")}
                                     trigger={<Flex>{exportButton}</Flex>}/>
                            :
                            <Flex>{exportButton}</Flex>
                    }
                </Flex>
            </Flex>
        );
    }

    renderActionsTab = () => {
        // Bar options
        const options = {
            responsive: true,
            scales: {
                y: {
                    ticks: {
                        stepSize: 1,
                    },
                },
            },
            plugins: {
                legend: {display: false},
                title: {
                    display: true,
                    text: languageObject.translate("ButtonUsageTitle"),
                },
            },
        };

        // Header for buttons statistics
        const buttonsTableHeader = {
            items: [
                languageObject.translate("Language"),
                languageObject.translate("ButtonTitle"),
                languageObject.translate("ButtonURL"),
                languageObject.translate("NumberOfClicks"),
            ],
            className: "table_header",
        };
        // Content for buttons statistics
        const buttonsTableContent = new Array<TableObject>();
        [
            this.state.message.defaultLanguage,
            ...this.state.message.translations,
        ].forEach((msgLanguage) => {
            if (msgLanguage.buttons.length > 0) {
                const languageTranslationId = this.context.languages.list.find((l) => l.id === msgLanguage.id)?.languageName ?? "";
                const languageLabel = languageObject.translate(languageTranslationId);
                const items = msgLanguage.buttons.map((btn) => {
                    const numberClicks = this.state.message.userClicks?.filter((item) => item.buttonId === btn.id).length ?? 0;
                    return {
                        key: "button_stats_" + btn.id,
                        items: [
                            languageLabel,
                            btn.title ?? "",
                            btn.link ?? "",
                            numberClicks + "",
                        ].map((s) => {
                            const isLink = s.startsWith("https://");
                            return {
                                content: isLink
                                    ? ((
                                        <Text
                                            content={s}
                                            color={"brand"}
                                            styles={{cursor: "pointer"}}
                                            onClick={() => window.open(s, "_blank")?.focus()}
                                        />
                                    ) as JSX.Element)
                                    : (s as string),
                                truncateContent: true as boolean,
                                key: (btn.id + "_" + s) as string,
                            } as ItemObject;
                        }),
                    };
                });

                buttonsTableContent.push(...items);
            }
        });

        const items = buttonsTableContent.map(
            (o) =>
                o.items[1].content.toString() + " - " + o.items[0].content.toString()
        );
        const buttons_value = buttonsTableContent.map((o) =>
            parseInt(o.items[3].content as string)
        );
        // Bar data
        const data = {
            labels: items,
            datasets: [
                {
                    data: buttons_value,
                    borderWidth: 0,
                    backgroundColor: buttons_value.map((b, i) =>
                        i % 2 ? brandColors[300] : brandColors[500]
                    ),
                    datalabels: {
                        color: "white",
                    },
                },
            ],
        };

        return (
            <Flex
                fill
                column
                styles={{marginTop: buttonsTableContent.length > 0 ? "30px" : "0"}}
            >
                {buttonsTableContent.length > 0 ? (
                    <>
                        <Bar
                            data={data}
                            options={options}
                            style={{maxHeight: "250px", marginBottom: "20px"}}
                        />
                        <Table
                            compact
                            header={buttonsTableHeader}
                            rows={buttonsTableContent}
                        />
                    </>
                ) : (
                    <Flex fill gap={"gap.small"} hAlign={"center"} vAlign={"center"}>
                        <Header
                            as={"h3"}
                            content={languageObject.translate("NoAvailableButton")}
                        />
                    </Flex>
                )}
            </Flex>
        );
    };
}

export interface ItemObject {
    key: string;
    content: JSX.Element | string;
    truncateContent: boolean;
}

export interface TableObject {
    key: string;
    items: Array<ItemObject>;
}

interface PieOptions {
    maintainAspectRatio: boolean;
    responsive: boolean;
    plugins: {
        legend: {
            onClick: (e: any) => void;
            position: string;
            labels: {
                color?: string;
                display?: boolean;
                position?: string;
                align?: string;
                maxHeight?: number;
                maxWidth?: number;
                font: {
                    family: string;
                    size: number | string;
                };
            };
        };
        title?: {
            padding?: string;
            position?: string;
            align?: string;
            color?: string;
            display?: boolean;
            text?: string;
        };
    };
}

export default StatusTaskModule;